import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  Action,
  Actions,
  ofActionDispatched,
  State,
  StateToken,
  type StateContext,
} from '@ngxs/store';
import {
  catchError,
  defer,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  shareReplay,
  tap,
} from 'rxjs';

import { Auth } from '@asi/auth/data-access-auth';
import type { SessionBase } from '@asi/auth/types-auth';
import { LocalStorageService } from '@cosmos/data-access-storage';
import { collectRouteParams } from '@cosmos/router';
import { handleLoginErrorRedirect } from '@customer-portal/auth/data-access-customer-portal-access-guard';

import { CustomerPortalCollectionAuthActions } from '../actions';
import { SednaAuthService } from '../services';
import {
  setCustomPortalCollectionAuthDataToLocalStorage,
  setCustomPortalProductAuthDataToLocalStorage,
} from '../utils/local-storage-auth-data';

export interface CustomerPortalCollectionAuthStateModel {
  session: SessionBase | null;
  // The `accessCode` is available only when the end-buyer is viewing the portal.
  accessCode: string | null;
}

export const CUSTOMER_PORTAL_COLLECTION_AUTH_STATE_TOKEN =
  new StateToken<CustomerPortalCollectionAuthStateModel>(
    'customerPortalCollectionAuth'
  );

@State<CustomerPortalCollectionAuthStateModel>({
  name: CUSTOMER_PORTAL_COLLECTION_AUTH_STATE_TOKEN,
  defaults: {
    session: null,
    accessCode: null,
  },
})
@Injectable()
export class CustomerPortalCollectionAuthState {
  private readonly _authService = inject(SednaAuthService);
  private readonly _localStorage = inject(LocalStorageService);
  private readonly _actions$ = inject(Actions);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);

  private readonly _routeParams$ = this._router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => collectRouteParams(this._route.snapshot)),
    distinctUntilChanged(),
    shareReplay(1)
  );

  private readonly _collectionId$ = this._routeParams$.pipe(
    map((params) => params['collectionId'] ?? null),
    distinctUntilChanged()
  );

  private readonly _productId$ = this._routeParams$.pipe(
    map((params) => {
      // Ignore cases when collectionId is present cause that means collection scope,
      // we are only interested in shared productId
      return !params['collectionId'] ? params['productId'] || null : null;
    }),
    distinctUntilChanged()
  );

  constructor() {
    this._actions$
      .pipe(ofActionDispatched(Auth.Logout), takeUntilDestroyed())
      .subscribe((action) => {
        // when api throws 401 (invalid accessToken) interceptor triggers logout action, we should redirect to unauthorized page
        if (action.options?.is401) {
          this._router.navigate(['/unauthorized'], {
            skipLocationChange: true,
          });
        }
      });
  }

  @Action(CustomerPortalCollectionAuthActions.LoginWithAccessCode, {
    cancelUncompleted: true,
  })
  private _loginWithAccessCode(
    ctx: StateContext<CustomerPortalCollectionAuthStateModel>,
    {
      payload,
    }: InstanceType<CustomerPortalCollectionAuthActions['LoginWithAccessCode']>
  ) {
    ctx.patchState({ accessCode: payload.accessCode });
    this._syncAuthData(ctx);

    return defer(() => this._authService.login(payload)).pipe(
      tap((session: SessionBase) =>
        ctx.dispatch(
          new CustomerPortalCollectionAuthActions.LoginSuccess(session)
        )
      ),
      catchError((error) => {
        return handleLoginErrorRedirect(error, this._router);
      })
    );
  }

  @Action([CustomerPortalCollectionAuthActions.LoginSuccess])
  private _updateSession(
    ctx: StateContext<CustomerPortalCollectionAuthStateModel>,
    action: InstanceType<CustomerPortalCollectionAuthActions['LoginSuccess']>
  ) {
    const session = {
      ...action.session,
      expires_at: null as number | null,
    };

    ctx.patchState({ session });
    this._syncAuthData(ctx);
  }

  @Action([CustomerPortalCollectionAuthActions.RestoreFromLocalStorage])
  private _restoreFromLocalStorage(
    ctx: StateContext<CustomerPortalCollectionAuthStateModel>,
    action: InstanceType<
      CustomerPortalCollectionAuthActions['RestoreFromLocalStorage']
    >
  ) {
    ctx.patchState({ session: action.session, accessCode: action.accessCode });
  }

  private async _syncAuthData(
    ctx: StateContext<CustomerPortalCollectionAuthStateModel>
  ) {
    const s = ctx.getState();
    const payload = {
      accessCode: s.accessCode ?? undefined,
      session: s.session ?? undefined,
    };

    const collectionId = await firstValueFrom(this._collectionId$, {
      defaultValue: null,
    });
    if (collectionId) {
      setCustomPortalCollectionAuthDataToLocalStorage(
        this._localStorage,
        collectionId,
        payload
      );
      return;
    }

    const productId = await firstValueFrom(this._productId$, {
      defaultValue: null,
    });
    if (productId) {
      setCustomPortalProductAuthDataToLocalStorage(
        this._localStorage,
        productId,
        payload
      );
    }
  }
}
